/**
 * Scroll Top Indicator
 * 
 * Creates a scroll progress indicator that shows:
 * - A gray track representing 100% of the website height
 * - A black thumb representing the current viewport position
 */

class ScrollTopIndicator {
  constructor(indicatorEl) {
    this.indicator = indicatorEl || document.querySelector('.indicator');
    if (!this.indicator) return;

    this.label = this.indicator.querySelector('.indicator__label');
    this.track = this.indicator.querySelector('.indicator__track');
    this.thumb = this.indicator.querySelector('.indicator__thumb');
    
    if (!this.track || !this.thumb) return;

    this.scrollContainer = this.getScrollContainer();
    this.threshold = this.getThreshold();
    this.handleClick = this.handleClick.bind(this);

    this.trackHeight = 150;
    this.thumbHeight = 0;

    this.init();
  }

  getScrollContainer() {
    // Theme layout uses an internal scroll container (off-canvas main content).
    return document.querySelector('.js-reading-content') || document.querySelector('.off-canvas__main');
  }

  getThreshold() {
    const attr = this.indicator.getAttribute('data-scrolltop-threshold');
    const parsed = attr ? parseInt(attr, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : 300;
  }

  init() {
    this.calculateDimensions();
    this.updateIndicator();

    this.indicator.addEventListener('click', this.handleClick, { passive: false });

    // Update on scroll and resize
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateIndicator();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Lenis can change which element actually scrolls (window vs internal container).
    // Listen on both to ensure updates always run.
    window.addEventListener('scroll', handleScroll, { passive: true });
    if (this.scrollContainer) {
      this.scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    // If Lenis is available, prefer its own scroll event (more reliable than DOM scroll state).
    if (window.lenis && typeof window.lenis.on === 'function') {
      window.lenis.on('scroll', (event) => {
        if (event && typeof event.scroll === 'number') {
          this.lenisScrollTop = event.scroll;
        }
        if (event && typeof event.limit === 'number') {
          this.lenisLimit = event.limit;
        }
        handleScroll();
      });
    }

    window.addEventListener('resize', () => {
      this.calculateDimensions();
      this.updateIndicator();
    });
  }

  handleClick(event) {
    event.preventDefault();

    if (window.lenis && typeof window.lenis.scrollTo === 'function') {
      window.lenis.scrollTo(0);
      return;
    }

    if (this.scrollContainer) {
      if (typeof this.scrollContainer.scrollTo === 'function') {
        this.scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      this.scrollContainer.scrollTop = 0;
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  calculateDimensions() {
    const { viewportHeight, documentHeight } = this.getScrollMetrics();

    // Thumb height represents viewport as percentage of document height
    const minThumbHeight = 30;
    this.thumbHeight = Math.max(
      minThumbHeight,
      (this.trackHeight * viewportHeight) / documentHeight
    );

    this.thumb.style.height = `${this.thumbHeight}px`;
  }

  updateIndicator() {
    const { scrollTop, viewportHeight, documentHeight } = this.getScrollMetrics();

    const scrollableDistance = documentHeight - viewportHeight;
    const scrollProgress = scrollableDistance > 0 ? scrollTop / scrollableDistance : 0;
    
    const maxThumbPosition = this.trackHeight - this.thumbHeight;
    const thumbPosition = Math.max(0, Math.min(maxThumbPosition, scrollProgress * maxThumbPosition));

    this.thumb.style.top = `${thumbPosition}px`;

    const shouldShow = scrollTop >= this.threshold && scrollableDistance > 0;
    this.indicator.classList.toggle('indicator--is-visible', shouldShow);
  }

  getScrollMetrics() {
    // 1) Prefer Lenis when present (it can virtualize scroll position).
    if (typeof this.lenisScrollTop === 'number' && typeof this.lenisLimit === 'number') {
      const viewportHeight = window.innerHeight;
      const documentHeight = viewportHeight + this.lenisLimit;
      return { scrollTop: this.lenisScrollTop, viewportHeight, documentHeight };
    }

    // 2) If internal container is scrollable, use it.
    if (this.scrollContainer && this.scrollContainer.scrollHeight > this.scrollContainer.clientHeight) {
      return {
        scrollTop: this.scrollContainer.scrollTop,
        viewportHeight: this.scrollContainer.clientHeight,
        documentHeight: this.scrollContainer.scrollHeight
      };
    }

    // 3) Fallback to window scroll.
    return {
      scrollTop: window.pageYOffset || document.documentElement.scrollTop || 0,
      viewportHeight: window.innerHeight,
      documentHeight: Math.max(document.body.scrollHeight, document.documentElement.scrollHeight)
    };
  }

  // No extra positioning adjustments needed; let CSS handle sticky behavior
}

// Initialize immediately to bind handler before cursor
const initScrollTop = () => {
  const indicator = document.querySelector('.indicator');
  if (!indicator) {
    // Try again after DOM is ready if element doesn't exist yet
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initScrollTop, { once: true });
      return;
    }
    return;
  }

  if (indicator.dataset.scrollTopInit === 'true') return;
  indicator.dataset.scrollTopInit = 'true';

  new ScrollTopIndicator(indicator);
};

// Start immediately
initScrollTop();

// Export for manual initialization
export default ScrollTopIndicator;

